import {
  type AssessmentSelectDto,
  AssessmentType,
  type CyberAssessmentSelectDto,
  CyberAssessmentStatus,
  type TechnologySelectDto,
  UserFeatureFlag,
} from '@@types/api.types';
import { useAssessmentsQueryOptions } from '@queries/assessment.query';
import {
  useDeleteCyberAssessment,
  useGetCyberAssessment,
  useGetTechnologyCyberAssessments,
  usePatchCyberAssessmentEvidencePackChecklistItem,
} from '@queries/cyber-assessment.query';
import { useGetCurrentUser } from '@queries/users.query';
import { surveySidebarStore } from '@stores/survey-sidebar.store';
import { useQuery } from '@tanstack/react-query';
import {
  buildCyberAssessmentSidebarModel,
  getCompletedMilitaryTrlSourceAssessments,
} from '@utils/cyber-assessment/model.util';
import {
  type CyberAssessmentReportSection,
  parseCyberAssessmentReportSection,
} from '@utils/cyber-assessment/report.util';
import { isChildOrganizationView as getIsChildOrganizationView } from '@utils/parent-organization.util';
import { createParser, parseAsString, useQueryState } from 'nuqs';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  canManageTechnologyCyberAssessments,
  getTechnologyCyberEmptyStateMessage,
  shouldUseTechnologyCyberFullWidthEmptyState,
} from './technology-cyber-tab.util';

const parseAsCyberSection = createParser<CyberAssessmentReportSection>({
  parse(queryValue: string) {
    return parseCyberAssessmentReportSection(queryValue);
  },
  serialize(value) {
    return value;
  },
});

type UseTechnologyCyberTabParams = {
  technology: TechnologySelectDto;
};

type UseTechnologyCyberTabResult = {
  canCreate: boolean;
  createDisabledReason?: string;
  cyberSection: CyberAssessmentReportSection;
  cyberSidebarModel: ReturnType<typeof buildCyberAssessmentSidebarModel<CyberAssessmentSelectDto>>;
  effectiveCyberAssessment: CyberAssessmentSelectDto | null;
  emptyStateMessage?: string;
  hasCyberAssessmentsTool: boolean;
  isCreateModalOpen: boolean;
  isDetailPending: boolean;
  isEmptyState: boolean;
  isPending: boolean;
  isFullWidthEmptyState: boolean;
  militaryTrlAssessments: AssessmentSelectDto[];
  openCreateModal: () => void;
  patchEvidencePackChecklistItem: ReturnType<typeof usePatchCyberAssessmentEvidencePackChecklistItem>['mutateAsync'];
  selectedCyberAssessmentId: string | null;
  setIsCreateModalOpen: (isOpen: boolean) => void;
  setSelectedAssessmentId: (assessmentId: string) => void;
  handleCreated: (createdCyberAssessmentId: string) => Promise<void>;
  handleDiscardDraft: (assessmentId: string) => Promise<void>;
  handleSectionChange: (value: string) => Promise<void>;
  navigateToDraftAssessment: (assessmentId: string) => void;
};

export const useTechnologyCyberTab = (params: UseTechnologyCyberTabParams): UseTechnologyCyberTabResult => {
  const { technology } = params;
  const navigate = useNavigate();
  const [cyberId, setCyberId] = useQueryState('cyberId', parseAsString);
  const [cyberSection, setCyberSection] = useQueryState('cyberSection', parseAsCyberSection.withDefault('fullReport'));
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { data: cyberAssessments = [], isPending } = useGetTechnologyCyberAssessments(technology.id);
  const { mutateAsync: deleteCyberAssessment } = useDeleteCyberAssessment();
  const { mutateAsync: patchEvidencePackChecklistItem } = usePatchCyberAssessmentEvidencePackChecklistItem();
  const { data: user, isSelectedCompanyParentOrganization, selectedCompany } = useGetCurrentUser();
  const assessmentsQueryOptions = useAssessmentsQueryOptions({
    technologyId: technology.id,
    type: [AssessmentType.TRL],
  });
  const { data: assessments = [] } = useQuery(assessmentsQueryOptions);
  const militaryTrlAssessments = useMemo(() => getCompletedMilitaryTrlSourceAssessments(assessments), [assessments]);
  const cyberSidebarModel = useMemo(() => {
    return buildCyberAssessmentSidebarModel({
      assessments: cyberAssessments,
      selectedAssessmentId: cyberId,
    });
  }, [cyberAssessments, cyberId]);
  const selectedCyberAssessment = cyberSidebarModel.selectedAssessment;
  const selectedCyberAssessmentId = selectedCyberAssessment?.id ?? null;
  const { data: cyberAssessmentDetail, isPending: isCyberAssessmentDetailPending } =
    useGetCyberAssessment(selectedCyberAssessmentId);

  const hasCyberAssessmentsTool = user?.featureFlags?.includes(UserFeatureFlag.CYBER_ASSESSMENTS_TOOL) ?? false;
  const isChildOrganizationView = getIsChildOrganizationView({
    isSelectedCompanyParentOrganization,
    selectedCompanyId: selectedCompany?.id,
    technologyCompanyId: technology.company?.id,
  });
  const canCreate = canManageTechnologyCyberAssessments({
    isChildOrganizationView,
    role: user?.profile?.role,
  });
  const createDisabledReason =
    militaryTrlAssessments.length === 0
      ? 'Complete a Military TRL assessment before creating a Cyber Assessment.'
      : undefined;
  const emptyStateMessage = getTechnologyCyberEmptyStateMessage({
    hasMilitaryTrlAssessments: militaryTrlAssessments.length > 0,
    isChildOrganizationView,
    role: user?.profile?.role,
  });
  const isFullWidthEmptyState = shouldUseTechnologyCyberFullWidthEmptyState({
    isChildOrganizationView,
    role: user?.profile?.role,
  });

  useEffect(() => {
    if (isPending || !cyberId || selectedCyberAssessmentId) {
      return;
    }

    void setCyberId(cyberSidebarModel.historyAssessments[0]?.id ?? null);
  }, [cyberId, cyberSidebarModel.historyAssessments, isPending, selectedCyberAssessmentId, setCyberId]);

  const navigateToDraftAssessment = (assessmentId: string): void => {
    surveySidebarStore.getState().reset();
    navigate(
      `/technology/${technology.id}/cyber-assessment/${assessmentId}?viewFirstUnansweredQuestion=true&returnTo=${encodeURIComponent(`/technology/${technology.id}?tab=cyber&cyberId=${assessmentId}`)}`,
    );
  };

  const handleSectionChange = async (value: string): Promise<void> => {
    const nextSection = parseCyberAssessmentReportSection(value);

    if (!nextSection) {
      return;
    }

    await setCyberSection(nextSection);
  };

  const handleCreated = async (createdCyberAssessmentId: string): Promise<void> => {
    await setCyberId(createdCyberAssessmentId);
    navigateToDraftAssessment(createdCyberAssessmentId);
  };

  const handleDiscardDraft = async (assessmentId: string): Promise<void> => {
    const nextSelectedAssessmentId = cyberSidebarModel.historyAssessments[0]?.id ?? null;

    await deleteCyberAssessment({
      cyberAssessmentId: assessmentId,
      technologyId: technology.id,
    });
    await setCyberId(nextSelectedAssessmentId);
  };

  const effectiveCyberAssessment = cyberAssessmentDetail ?? selectedCyberAssessment;
  const isDetailPending = Boolean(selectedCyberAssessmentId) && isCyberAssessmentDetailPending;
  const isEmptyState =
    !effectiveCyberAssessment ||
    (Boolean(selectedCyberAssessmentId) && !cyberAssessmentDetail) ||
    effectiveCyberAssessment.status === CyberAssessmentStatus.DRAFT;

  return {
    canCreate,
    createDisabledReason,
    cyberSection,
    cyberSidebarModel,
    effectiveCyberAssessment,
    emptyStateMessage,
    handleCreated,
    handleDiscardDraft,
    handleSectionChange,
    hasCyberAssessmentsTool,
    isCreateModalOpen,
    isDetailPending,
    isEmptyState,
    isFullWidthEmptyState,
    isPending,
    militaryTrlAssessments,
    navigateToDraftAssessment,
    openCreateModal: () => setIsCreateModalOpen(true),
    patchEvidencePackChecklistItem,
    selectedCyberAssessmentId,
    setIsCreateModalOpen,
    setSelectedAssessmentId: (assessmentId: string) => {
      void setCyberId(assessmentId);
    },
  };
};
